import React, { useState } from 'react';
import {
    useGetApplicationsQuery,
    useLazyGetSsoUrlQuery,
    Application,
    useGetUserInfoQuery
} from '../../slices/account-api.slice';
import { FallBack } from '../../shared/loading-fallback/loading-fallback';
import styles from './app-tiles.module.scss';
import { autoId } from '@workday/upc-common';
import { useTranslation } from 'react-i18next';
import { AppCard } from './app-card/app-card';
import { GroupButton } from './group-button/group-button';
import { dashboardIcon } from '@workday/canvas-system-icons-web';
import { Flex } from '@workday/canvas-kit-react';
import { ID_APP_TILES_CONTAINER, ID_APP_TILES_GROUPED_TILES, ID_APP_TILES_SIDEBAR } from '../../autoIds';
import { NoApps } from './no-apps';
import { LearnWhyToolTip } from './learn-why-tooltip';
import { selectIsVcrEnvironment } from '../../slices/login.slice';
import { useSelector } from 'react-redux';

type TileGroup = {
    name: string;
    applications: Array<Application>;
};

/* Group tiles by customer name and then sort alphabetically
        groupedTiles = [
            {name: 'Customer Name',
            applications: [
                app1,
                app2
                ...
            ]},
            ...
        ]
     */
function processApplicationTiles(tiles: Array<Application> = []) {
    const groupedTiles: TileGroup[] = [];

    tiles.forEach(tile => {
        const customer = tile.customer;
        const groupIndex = groupedTiles.findIndex(group => group.name === customer);

        if (groupIndex !== -1) {
            groupedTiles[groupIndex].applications.push(tile);
        } else {
            groupedTiles.push({
                name: customer,
                applications: [tile]
            });
        }
    });

    groupedTiles.sort((group1, group2) => {
        return group1.name < group2.name ? -1 : 1;
    });

    const tileGroups = groupedTiles.map(group => group.name);

    return { tiles, groupedTiles, tileGroups };
}

export const AppTiles = () => {
    const { data: applications, isFetching: isApplicationsFetching, isSuccess } = useGetApplicationsQuery();

    const [getSsoUrlQuery] = useLazyGetSsoUrlQuery();

    const [filterGroups, setFilterGroups] = useState([]);

    const { tiles, groupedTiles, tileGroups } = processApplicationTiles(applications?.tiles);

    const onApplicationClick = async ({ appId, isEnvApp }: Application) => {
        const newWindow = window.open(' ', '_blank');
        const result = await getSsoUrlQuery({ applicationId: appId, isEnvApp });
        if (newWindow) {
            newWindow.document.title = 'Loading...';
            newWindow.opener = null; // remove opener so other window cannot refer back
            if (result.isSuccess) {
                newWindow.location = result.data.redirectUri;
                newWindow.focus();
            } else {
                newWindow.close();
            }
        }
    };

    return (
        <div {...autoId(ID_APP_TILES_CONTAINER)}>
            {isApplicationsFetching ? (
                <FallBack />
            ) : (
                <div className={styles.appTiles}>
                    {isSuccess && tiles.length === 0 ? (
                        <NoApps />
                    ) : (
                        <Flex columnGap='m' height='100%'>
                            <Flex.Item height='100%'>
                                <Sidebar
                                    onSelectFilterGroups={setFilterGroups}
                                    filterGroups={filterGroups}
                                    tileGroups={tileGroups}
                                />
                            </Flex.Item>
                            <Flex.Item height='100%' className={styles.rightColumn}>
                                <Fade />
                                <GroupedTiles
                                    filterGroups={filterGroups}
                                    groupedTiles={groupedTiles}
                                    onApplicationClick={onApplicationClick}
                                />
                            </Flex.Item>
                        </Flex>
                    )}
                </div>
            )}
        </div>
    );
};

const Fade = () => {
    return <div className={styles.fade} />;
};

const Sidebar = ({ onSelectFilterGroups, filterGroups, tileGroups }) => {
    const { t } = useTranslation('account');

    const clearFilters = () => {
        onSelectFilterGroups([]);
    };

    const isFilterOn = groupName => {
        return filterGroups.includes(groupName);
    };

    // Remove group filters when clicked
    const onAllAppsButtonClick = () => {
        clearFilters();
    };

    const onGroupButtonClick = groupName => {
        // Toggle button if it's already clicked
        if (filterGroups.includes(groupName)) {
            onSelectFilterGroups(filterGroups.filter(name => name !== groupName));
        } else {
            onSelectFilterGroups([groupName]);
        }
    };

    return (
        <div {...autoId(ID_APP_TILES_SIDEBAR)} className={styles.sidebar}>
            <h2 className={styles.sidebarTitle}>{t('AppTiles.myCustomers')}</h2>
            <hr className={styles.brandedUnderline} />
            <p className={styles.sidebarSubtitle}>{t('AppTiles.filterYourApplications')}</p>
            <Flex as='ul' flexDirection={'column'} className={styles.buttonContainer}>
                <li>
                    <GroupButton
                        onGroupButtonClick={onAllAppsButtonClick}
                        isToggled={filterGroups.length === 0}
                        icon={dashboardIcon}>
                        {t('AppTiles.allCustomers')}
                    </GroupButton>
                </li>
                <li className={styles.scrollableGroupButtonArea}>
                    <Flex as='ul' flexDirection={'column'} className={styles.scrollableGroupButtonAreaContent}>
                        {tileGroups.map(groupName => (
                            <li key={groupName}>
                                <GroupButton
                                    onGroupButtonClick={onGroupButtonClick}
                                    isToggled={isFilterOn(groupName)}
                                    key={groupName}>
                                    {groupName}
                                </GroupButton>
                            </li>
                        ))}
                    </Flex>
                </li>
            </Flex>
        </div>
    );
};

const GroupedTiles = ({ filterGroups, groupedTiles, onApplicationClick }) => {
    const { data: userInfo } = useGetUserInfoQuery();
    const isVcrEnvironment = useSelector(selectIsVcrEnvironment);
    const displayAccountResetTooltip = !!userInfo?.accountResetDate && !isVcrEnvironment;

    const Tiles = tiles => {
        return tiles.map(app => <AppCard key={app.appId} app={app} onAppCardClick={onApplicationClick} />);
    };

    const TileGroup = group => {
        return filterGroups.length === 0 || filterGroups.includes(group.name) ? (
            <div className={styles.tileGroup} key={`tileGroup-${group.name}`}>
                <h3>{group.name}</h3>
                <div className={styles.groupOfTiles}>{Tiles(group.applications)}</div>
            </div>
        ) : null;
    };

    return (
        <div {...autoId(ID_APP_TILES_GROUPED_TILES)} className={styles.groupedTiles}>
            <div className={styles.groupedTilesScrollbox}>{groupedTiles.map(group => TileGroup(group))}</div>
            {displayAccountResetTooltip ? <LearnWhyToolTip /> : null}
        </div>
    );
};
